import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Clock } from "lucide-react";
import { Logo } from "../components/brand/Logo";

const EASE = [0.16, 1, 0.3, 1];

// Bespoke v5 preview thumbs — each reflects the actual aesthetic of the direction
const ThumbA = () => (
    <svg viewBox="0 0 480 270" className="h-full w-full" aria-hidden="true">
        {/* Limestone bg */}
        <rect width="480" height="270" fill="#FAF9F6" />
        {/* Asir stripe top */}
        <g>
            <rect x="0" y="0" width="40" height="6" fill="#8A1E12" />
            <rect x="40" y="0" width="40" height="6" fill="#E59500" />
            <rect x="80" y="0" width="40" height="6" fill="#0D525C" />
            <rect x="120" y="0" width="40" height="6" fill="#1A1C1D" />
            <rect x="160" y="0" width="40" height="6" fill="#FAF9F6" />
            <rect x="200" y="0" width="40" height="6" fill="#8A1E12" />
            <rect x="240" y="0" width="40" height="6" fill="#E59500" />
            <rect x="280" y="0" width="40" height="6" fill="#0D525C" />
            <rect x="320" y="0" width="40" height="6" fill="#1A1C1D" />
            <rect x="360" y="0" width="40" height="6" fill="#FAF9F6" />
            <rect x="400" y="0" width="40" height="6" fill="#8A1E12" />
            <rect x="440" y="0" width="40" height="6" fill="#E59500" />
        </g>
        {/* Headline */}
        <text x="32" y="86" fill="#1A1C1D" fontFamily="Outfit, sans-serif" fontSize="32" fontWeight="700" letterSpacing="-1">What will you</text>
        <text x="32" y="124" fill="#8A1E12" fontFamily="Outfit, sans-serif" fontSize="32" fontWeight="700" letterSpacing="-1">call</text>
        <text x="105" y="124" fill="#1A1C1D" fontFamily="Outfit, sans-serif" fontSize="32" fontWeight="700" letterSpacing="-1">it?</text>
        {/* Search */}
        <rect x="32" y="155" width="220" height="38" rx="8" fill="#FFFFFF" stroke="#1A1C1D" strokeWidth="2" />
        <rect x="190" y="161" width="56" height="26" rx="6" fill="#8A1E12" />
        <text x="208" y="178" fill="#FFFFFF" fontFamily="Outfit, sans-serif" fontSize="11" fontWeight="700">Search</text>
        {/* Pillars right */}
        <rect x="280" y="58" width="170" height="56" rx="10" fill="#8A1E12" />
        <text x="294" y="92" fill="#FFFFFF" fontFamily="Outfit" fontSize="14" fontWeight="700">Accredited .sa</text>
        <rect x="280" y="124" width="170" height="56" rx="10" fill="#E59500" />
        <text x="294" y="160" fill="#1A1C1D" fontFamily="Outfit" fontSize="20" fontWeight="800">&lt; 60s</text>
        <rect x="280" y="190" width="170" height="56" rx="10" fill="#0D525C" />
        <text x="294" y="224" fill="#FFFFFF" fontFamily="Outfit" fontSize="18" fontWeight="800">240,000+</text>
        {/* TLD chips */}
        <rect x="32" y="208" width="44" height="22" rx="4" fill="#1A1C1D" />
        <text x="44" y="224" fill="#FFFFFF" fontFamily="JetBrains Mono, monospace" fontSize="10" fontWeight="600">.sa</text>
        <rect x="80" y="208" width="56" height="22" rx="4" fill="none" stroke="#C7CCD4" strokeWidth="1.5" />
        <text x="88" y="224" fill="#1A1C1D" fontFamily="JetBrains Mono, monospace" fontSize="10" fontWeight="600">.com.sa</text>
    </svg>
);

const ThumbB = () => (
    <svg viewBox="0 0 480 270" className="h-full w-full" aria-hidden="true">
        <rect width="480" height="270" fill="#0D525C" />
        {/* Diamond pattern overlay */}
        <defs>
            <pattern id="diam" width="22" height="22" patternUnits="userSpaceOnUse">
                <path d="M11 0L22 11L11 22L0 11Z" fill="none" stroke="rgba(229,149,0,0.18)" strokeWidth="0.6" />
                <circle cx="11" cy="11" r="1.2" fill="rgba(229,149,0,0.4)" />
            </pattern>
        </defs>
        <rect width="480" height="270" fill="url(#diam)" />
        {/* Souq glow */}
        <circle cx="430" cy="40" r="80" fill="rgba(229,149,0,0.15)" />
        {/* Headline */}
        <text x="32" y="100" fill="#FFFFFF" fontFamily="Outfit, sans-serif" fontSize="38" fontWeight="800" letterSpacing="-1">What will you</text>
        <text x="32" y="138" fill="#E59500" fontFamily="Outfit, sans-serif" fontSize="38" fontWeight="800" letterSpacing="-1">call it?</text>
        {/* Glass search */}
        <rect x="32" y="166" width="240" height="40" rx="8" fill="rgba(255,255,255,0.08)" stroke="#E59500" strokeWidth="2" />
        <rect x="218" y="172" width="48" height="28" rx="6" fill="#E59500" />
        <text x="226" y="190" fill="#1A1C1D" fontFamily="Outfit, sans-serif" fontSize="11" fontWeight="700">Search</text>
        {/* Right glass stack */}
        <rect x="290" y="58" width="170" height="58" rx="8" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
        <text x="304" y="80" fill="#E59500" fontFamily="Outfit" fontSize="9" fontWeight="700">SAUDINIC · ZATCA · PDPL</text>
        <text x="304" y="105" fill="#FFFFFF" fontFamily="Outfit" fontSize="20" fontWeight="800">240K+ · 99.99 · 24/7</text>
        <rect x="290" y="124" width="170" height="76" rx="8" fill="rgba(0,0,0,0.4)" stroke="rgba(255,255,255,0.18)" />
        <text x="304" y="142" fill="#E59500" fontFamily="JetBrains Mono" fontSize="10" fontWeight="700">A    185.199.108.153</text>
        <text x="304" y="158" fill="#E59500" fontFamily="JetBrains Mono" fontSize="10" fontWeight="700">MX   10 mail.nour.net</text>
        <text x="304" y="174" fill="#E59500" fontFamily="JetBrains Mono" fontSize="10" fontWeight="700">TXT  v=spf1 ~all</text>
        <text x="304" y="194" fill="#E59500" fontFamily="Outfit" fontSize="9" fontWeight="700">● LIVE · 8s</text>
        <rect x="290" y="208" width="170" height="38" rx="8" fill="#8A1E12" />
        <text x="304" y="231" fill="#FFFFFF" fontFamily="Outfit" fontSize="13" fontWeight="700">Saudi-hosted</text>
    </svg>
);

const ThumbC = () => (
    <svg viewBox="0 0 480 270" className="h-full w-full" aria-hidden="true">
        <rect width="480" height="270" fill="#FAF9F6" />
        {/* Top masthead rule */}
        <line x1="32" y1="32" x2="448" y2="32" stroke="#1A1C1D" strokeWidth="2" />
        <text x="32" y="26" fill="#1A1C1D" fontFamily="JetBrains Mono" fontSize="9" fontWeight="700" letterSpacing="2">№ 012 · NOURNET FIELD NOTES</text>
        <text x="448" y="26" textAnchor="end" fill="#6B7280" fontFamily="JetBrains Mono" fontSize="9" fontWeight="700" letterSpacing="2">RIYADH · FEB 2026</text>
        {/* Massive .sa */}
        <text x="20" y="220" fill="#1A1C1D" fontFamily="Outfit, sans-serif" fontSize="220" fontWeight="800" letterSpacing="-12">.sa</text>
        {/* Right column */}
        <text x="280" y="62" fill="#8A1E12" fontFamily="JetBrains Mono" fontSize="9" fontWeight="700" letterSpacing="2">FILED FROM RIYADH</text>
        <text x="280" y="100" fill="#1A1C1D" fontFamily="Outfit" fontSize="22" fontWeight="800" letterSpacing="-1">What will you</text>
        <text x="280" y="124" fill="#8A1E12" fontFamily="Outfit" fontSize="22" fontWeight="800" letterSpacing="-1">call it?</text>
        {/* Drop-cap lede */}
        <text x="280" y="156" fill="#8A1E12" fontFamily="Outfit" fontSize="28" fontWeight="800">R</text>
        <text x="296" y="150" fill="#2D3133" fontFamily="Outfit" fontSize="10" fontWeight="500">egister .sa domains, orchestrate</text>
        <text x="280" y="166" fill="#2D3133" fontFamily="Outfit" fontSize="10" fontWeight="500">DNS, run Saudi-grade email.</text>
        {/* Search line */}
        <line x1="280" y1="190" x2="448" y2="190" stroke="#1A1C1D" strokeWidth="1" />
        <text x="280" y="184" fill="#1A1C1D" fontFamily="JetBrains Mono" fontSize="9" fontWeight="700" letterSpacing="2">SEARCH · THE REGISTRY</text>
        <text x="280" y="216" fill="#9CA3AF" fontFamily="Outfit" fontSize="14" fontWeight="600">Try your company name…</text>
        <line x1="280" y1="226" x2="448" y2="226" stroke="#1A1C1D" strokeWidth="3" />
        <rect x="400" y="208" width="48" height="20" rx="4" fill="#1A1C1D" />
        <text x="412" y="222" fill="#FFFFFF" fontFamily="Outfit" fontSize="9" fontWeight="700">Search</text>
        {/* Bottom byline rule */}
        <line x1="280" y1="244" x2="448" y2="244" stroke="#C7CCD4" strokeWidth="1" />
        <text x="280" y="258" fill="#6B7280" fontFamily="JetBrains Mono" fontSize="8" fontWeight="700" letterSpacing="2">FROM 180 SAR/YR · NO CARD · 240K+</text>
    </svg>
);

const DIRECTIONS = [
    { id: "a", to: "/home-a", thumb: <ThumbA />, ready: true },
    { id: "b", to: "/home-b", thumb: <ThumbB />, ready: true },
    { id: "c", to: "/home-c", thumb: <ThumbC />, ready: true },
];

export default function Preview() {
    const { t } = useTranslation();
    return (
        <main className="min-h-screen bg-limestone-100 pt-32" data-testid="preview-page">
            {/* Asir stripe */}
            <div className="asir-stripe h-[6px]" aria-hidden="true" />
            <div className="mx-auto max-w-[1320px] px-5 pb-24 pt-12 lg:px-8">
                <div className="max-w-3xl">
                    <p className="eyebrow">{t("preview.kicker")}</p>
                    <h1 className="section-headline mt-5" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
                        {t("preview.title")}
                    </h1>
                    <p className="body-prose mt-6 max-w-2xl">{t("preview.subtitle")}</p>
                </div>

                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {DIRECTIONS.map((d, i) => (
                        <motion.div
                            key={d.id}
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: EASE, delay: 0.1 + i * 0.08 }}
                        >
                            <Link
                                to={d.to}
                                data-testid={`preview-card-${d.id}`}
                                className="group block overflow-hidden rounded-card border-2 border-ink-200 bg-white transition-all duration-300 ease-nour hover:-translate-y-1 hover:border-ink-800 hover:shadow-lift"
                            >
                                <div className="aspect-[16/9] overflow-hidden border-b-2 border-ink-200">
                                    {d.thumb}
                                </div>
                                <div className="p-7">
                                    <div className="flex items-center justify-between">
                                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-ink-600">
                                            {t(`preview.${d.id}.label`)}
                                        </p>
                                        {d.ready ? (
                                            <span className="inline-flex items-center gap-1 rounded-[4px] bg-redsea-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                                                <Check className="h-3 w-3" strokeWidth={3} />
                                                {t("preview.statusReady")}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 rounded-[4px] bg-mudbrick-300 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ink-700">
                                                <Clock className="h-3 w-3" />
                                                {t("preview.statusPending")}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="mt-4 font-display text-[26px] font-bold tracking-tight text-ink-800">
                                        {t(`preview.${d.id}.name`)}
                                    </h3>
                                    <p className="mt-3 text-[16px] leading-relaxed font-medium text-ink-600">
                                        {t(`preview.${d.id}.desc`)}
                                    </p>
                                    <div className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-bold text-madder-500">
                                        <span className="border-b-2 border-madder-500 pb-0.5 transition-colors duration-200">
                                            {t("preview.open")}
                                        </span>
                                        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 rtl-mirror" />
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>

                <p className="mt-14 caption">{t("preview.footer")}</p>

                <div className="mt-8 flex items-center gap-3">
                    <Logo size={26} />
                    <span className="font-mono text-[12px] font-semibold text-ink-600">
                        NourNet · design review · v0.5
                    </span>
                </div>
            </div>
        </main>
    );
}
